/*!
 * Copyright 2020 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {Injectable, NgZone} from "@angular/core";
import {FirebaseApp} from "@angular/fire/app";
import {
  Messaging,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage
} from "@angular/fire/messaging";
import {PushMessage} from "@app/modules/messages/models/push-message";
import {PushMsgActions} from "@app/store/push-msg/push-msg.actions";
import {Store} from "@ngxs/store";
import {TrecTokenRef} from "@trec/api/token-ref";
import {C3ChatDocPatActions} from "@trec/app-modules/chat";
import {isBefore, subHours} from "date-fns";
import {FirebaseError} from "firebase/app";
import {BehaviorSubject, Subject, Subscription} from "rxjs";
import {filter, first} from "rxjs/operators";

export interface TrecPushMessage {
  service: "chat" | string | null;
  message: PushMessage;
}

export type PushNotificationStatus =
  | "unknown"
  | "need-permissions"
  | "just-accepted"
  | "accepted"
  | "rejected"
  | "error"
  | "unsupported";

@Injectable({
  providedIn: "root",
})
export class PushManagerService {
  // Public utils ////////////////////////////////////////////////////////////////////////////////

  private readonly _pushMessages$ = new Subject<TrecPushMessage>();
  readonly pushMessages$ = this._pushMessages$.asObservable();

  private readonly _pushStatus$ = new BehaviorSubject<PushNotificationStatus>("unknown");
  readonly pushStatus$ = this._pushStatus$.asObservable();

  // Private utils ////////////////////////////////////////////////////////////////////////////////

  private readonly messaging?: Messaging;

  /**
   * Vapid key loaded from general config.
   */
  private publicVapidKey?: string;

  /**
   * Flag for firebase messagging initialization status.
   */
  private FCM_INITIALIZED = false;

  private readonly FCM_TOKEN_SYNC_PERDIOD_HOURS = 12;
  readonly KEY_LAST_FCM_TOKEN_SYNC = "c3-last-fcm-sync";

  /**
   * Error codes thrown by Firebase if user does not allow notifications when browser popup alert appears.
   */
  private readonly FIREBASE_NOTIFICATION_REJECT_ERRORS = [
    "messaging/permission-blocked", // User reject notifications
    "messaging/notifications-blocked", // User reject notifications
    "messaging/permission-default", // User does not express any consensus or rejection
  ];
  private readonly FIREBASE_NOTIFICATION_ERROR_UNSUPPORTED_BROWSER =
    "messaging/unsupported-browser";
  // private readonly FIREBASE_NOTIFICATION_ERROR_FAILED_SW_REGISTRATION = 'messaging/failed-service-worker-registration';

  private fcmMessagesListenerSubscr?: () => void;

  private handlerSubscr?: Subscription;

  constructor(
    private store: Store,
    private firebaseApp: FirebaseApp,
    private ngZone: NgZone,
    private tokenRef: TrecTokenRef
  ) {
    try {
      this.messaging = getMessaging(this.firebaseApp);
    } catch (e) {
      if (!this.isErrUnsupportedBrowser(e)) {
        console.log(e);
      }
    }
  }

  async initFirebaseMessaging() {
    // Messaging is unsupported
    if (!this.messaging || !isSupported()) {
      this.updateStatus("unsupported");
      return;
    }

    try {
      if (this.FCM_INITIALIZED) {
        // Firebase messaging already initialized
        return;
      }

      this.FCM_INITIALIZED = true;

      if ((window as any).c3GetFirebaseConfigForCurrentEnv) {
        try {
          this.publicVapidKey = (window as any).c3GetFirebaseConfigForCurrentEnv().publicVapidKey;
        } catch (e) {
          // Ignore, vapidKey is not used
          console.log(e);
        }
      }

      // Immediately register for firebase foreground messages
      this.initListenForForegroundMessages();

      // Check if user granted permissions for push notifications
      this.updateStatus();

      // Check if sync token with server
      // Wait some time before synchronization
      setTimeout(() => {
        this.syncFcmToken();
      }, 15000); // 15sec

      // Listen for push notification and handle them
      this.startCommonPushHandling();
    } catch (e) {
      if (!this.isErrUnsupportedBrowser(e)) {
      }
    }
  }

  shutdownFirebaseMessaging() {
    this.fcmMessagesListenerSubscr?.();
    this.handlerSubscr?.unsubscribe();

    this.fcmMessagesListenerSubscr = undefined;
    this.handlerSubscr = undefined;

    this.FCM_INITIALIZED = false;
  }

  async invalidateCurrentFcmToken() {
    if (this.messaging) {
      // FCM enabled

      // Reset data about FCM token status
      localStorage.removeItem(this.KEY_LAST_FCM_TOKEN_SYNC);

      if (this.isPushActive()) {
        // If user accepted the notifications -> then, there should be a FCM token -> get it

        try {
          await deleteToken(this.messaging);
        } catch (e) { }
      }
      // No permissions -> probably there is no FCM token
    }
  }

  /**
   * Ask to user the permission for notifications.
   */
  async requestNotificationPermissions() {
    if (this.messaging) {
      // FCM enabled

      try {
        // getToken also ask for permissions
        const fcmToken = await this.getFCMToken();

        this.updateStatus(null, true);

        // Ok! Now retrieve FCM token and send to server
        await this.store.dispatch(new PushMsgActions.SendFCMToken({fcmToken})).toPromise();
      } catch (error) {
        const err: FirebaseError = error;

        // User reject permissions (or another error happen)
        if (this.FIREBASE_NOTIFICATION_REJECT_ERRORS.includes(err.code)) {
          // User reject
          this.updateStatus("rejected");
        } else if (this.isErrUnsupportedBrowser(err)) {
          // this.updateStatus('unsupported');
          // Do nothing (already done)
        } else {
          // Real error
          this.updateStatus("error");
        }
      }
    }
  }

  getStatus(): PushNotificationStatus {
    return this._pushStatus$.getValue();
  }

  // Private methods //////////////////////////////////////////////////////////////////////////////

  /**
   * Start listening for messages regitering to FCM onMessage callback.
   */
  private initListenForForegroundMessages() {
    if (this.messaging) {
      // FCM enabled

      // Unsubscribe from previous subscr
      this.fcmMessagesListenerSubscr?.();

      this.fcmMessagesListenerSubscr = onMessage(this.messaging, (payload) => {
        console.log('PAYLOAD FOREGROUND MESSAGES', payload);
        // Notify to interested components
        this._pushMessages$.next({
          service: payload.data?.service ?? null,
          message: payload as unknown as PushMessage,
        });
      });
    }
  }

  private updateStatus(forceNewState?: PushNotificationStatus | null, justAccepted = false) {
    if (this.getStatus() === "unsupported") {
      // Do not update -> the browser does not support FCM push notifications
      return;
    }

    if (forceNewState) {
      this._pushStatus$.next(forceNewState);
      return;
    }

    switch (Notification.permission) {
      case "granted":
        this._pushStatus$.next(justAccepted ? "just-accepted" : "accepted");
        break;
      case "denied":
        this._pushStatus$.next("rejected");
        break;
      case "default":
        this._pushStatus$.next("need-permissions");
        break;
      default:
        this._pushStatus$.next("unknown");
        break;
    }
  }

  private async syncFcmToken() {
    if (this.messaging) {
      // FCM enabled

      // Do it only if user is logged
      this.tokenRef
        .getAuthInfo()
        .pipe(
          filter((a) => !!a?.token), // Wait for token
          first() // Only once
        )
        .subscribe(async () => {
          const lastSyncIso = localStorage.getItem(this.KEY_LAST_FCM_TOKEN_SYNC);
          const lastSync = lastSyncIso ? new Date(lastSyncIso) : new Date(0); // 1970

          if (isBefore(lastSync, subHours(new Date(), this.FCM_TOKEN_SYNC_PERDIOD_HOURS))) {
            // FCM token should be updated -> try to sync with server

            if (this.isPushActive()) {
              try {
                const fcmToken = await this.getFCMToken();

                this.store.dispatch(new PushMsgActions.SendFCMToken({fcmToken}));
              } catch (e) {
                if (!this.isErrUnsupportedBrowser(e)) {
                  this.updateStatus("error");
                }
              }
            }
          }
        });
    }
  }

  private async getFCMToken(): Promise<string> {
    if (!this.messaging) {
      throw new Error(`"getFCMToken" was called before FCM initialization`);
    }

    // Change the service worker used -> as default firebase/messaging tries to load a "firebase-messaging-sw.js" file
    // but this SW does not exists since we use it in combination with the Angular SW toolkit.
    // https://github.com/angular/angularfire/blob/master/docs/messaging/messaging.md

    const swRegistration = await this.getSwRegistration();

    if (!swRegistration) {
      console.log("Try to get FCM token but without our SW registration");
    }

    return getToken(this.messaging, {
      vapidKey: this.publicVapidKey,
      serviceWorkerRegistration: swRegistration,
    });
  }

  private isPushActive(): boolean {
    return Notification.permission === "granted" && this.getStatus() !== "unsupported";
  }

  private isErrUnsupportedBrowser(err: FirebaseError | unknown): boolean {
    if ((err as FirebaseError)?.code === this.FIREBASE_NOTIFICATION_ERROR_UNSUPPORTED_BROWSER) {
      this.updateStatus("unsupported");
      return true;
    }
    return false;
  }

  private async getSwRegistration(): Promise<ServiceWorkerRegistration | undefined> {
    // Get current service worker registration
    return await navigator.serviceWorker.getRegistration();
  }

  // Push handlers //////////////////////////////////////////////////////////////////////////////

  startCommonPushHandling() {
    this.handlerSubscr?.unsubscribe();

    this.handlerSubscr = this.pushMessages$.subscribe((payload) => {
      console.log('PAYLOAD START COMMON PUSH', payload);
      switch (payload.service) {
        case "chat":
          this.ngZone.run(() => {
            this.store.dispatch(new C3ChatDocPatActions.FetchChats());
          });
          break;

        default:
          break;
      }
    });
  }

  stopCommonPushHandling() {
    // TODO better: should be able to inibit only handling of specific push sent by specific service
    // TODO better: should use mutex counter to check how many components requests to start/stop general handler

    this.handlerSubscr?.unsubscribe();
  }
}
